import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import '../assets/css/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setIsMobile(width <= 767);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize]);

  const handleOrientationChange = () => {
    setTimeout(handleResize, 100);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    e.currentTarget.style.transform = 'scale(0.98)';
    e.currentTarget.style.transition = 'transform 0.1s ease';
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    setTimeout(() => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.transition = 'transform 0.3s ease';
    }, 100);
  };

  return (
    <div className="privacy-page">
      <div className="privacy-container">
        {/* Header Section */}
        <div className="privacy-header">
          <div className="privacy-icon">
            <FaShieldAlt />
          </div>
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-subtitle">
            {isMobile 
              ? 'Your privacy matters to us. Learn how we protect your information.'
              : 'At Design Craft Office Furniture, your privacy is important to us. This Privacy Policy outlines how we collect, use, and protect your personal information when you visit our website www.designcraft.ae, interact with our services, or communicate with our team.'
            }
          </p>
        </div>

        {/* Content Section */}
        <div className="privacy-content">
          {/* Section 1 */}
          <div className="privacy-section">
            <h2>1. Information We Collect</h2>
            <p>We may collect personal information from you when you:</p>
            <ul>
              <li>Browse our website</li>
              <li>Submit an inquiry form or request a quote</li>
              <li>Contact us via email or phone</li>
              <li>Subscribe to our newsletter or marketing content</li>
            </ul>
            <p>The types of information may include:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company name</li>
              <li>Project or furniture requirements</li>
              <li>Any additional details you voluntarily provide</li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="privacy-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Respond to your inquiries and requests</li>
              <li>Provide product or service information</li>
              <li>Process and manage quotes or orders</li>
              <li>Improve our website and customer experience</li>
              <li>Send you updates, promotions, or newsletters (only if you opt-in)</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="privacy-section">
            <h2>3. Sharing of Information</h2>
            <p>We do not sell, rent, or trade your personal information. However, we may share your information with:</p>
            <ul>
              <li>Trusted service providers who assist in delivering our services (e.g., delivery partners, IT support)</li>
              <li>Legal authorities if required by law or to protect our rights</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="privacy-section">
            <h2>4. Cookies and Analytics</h2>
            <p>Our website may use cookies and third-party analytics tools (such as Google Analytics) to:</p>
            <ul>
              <li>Monitor website traffic and performance</li>
              <li>Understand user behavior and improve navigation</li>
              <li>Store user preferences for future visits</li>
            </ul>
            <p>You can disable cookies in your browser settings, but this may affect certain site features.</p>
          </div>

          {/* Section 5 */}
          <div className="privacy-section">
            <h2>5. Data Security</h2>
            <p>We take appropriate security measures to protect your personal data from unauthorized access, disclosure, or misuse. While no system is 100% secure, we strive to safeguard your information using industry-standard protocols.</p>
          </div>

          {/* Section 6 */}
          <div className="privacy-section">
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Request access to your personal data</li>
              <li>Ask for corrections or deletion of your data</li>
              <li>Opt-out of marketing communications at any time</li>
            </ul>
            <p>To do so, please email us at <a href="mailto:info@designcraft.ae">info@designcraft.ae</a>.</p>
          </div>

          {/* Section 7 */}
          <div className="privacy-section">
            <h2>7. Third-Party Links</h2>
            <p>Our website may contain links to other websites. We are not responsible for the privacy practices or content of third-party sites.</p>
          </div>

          {/* Section 8 */}
          <div className="privacy-section">
            <h2>8. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. All changes will be posted on this page with the revised date.</p>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="privacy-cta">
          <h3>Questions about this Privacy Policy?</h3>
          <p>
            {isMobile 
              ? 'Contact us for any privacy-related questions or concerns.'
              : 'If you have any questions about this Privacy Policy or how your data is handled, please don\'t hesitate to reach out to our team.'
            }
          </p>
          
          <div className="contact-info">
            <div className="contact-item">
              <FaMapMarkerAlt className="contact-icon" />
              <div>
                <strong>Design Craft Office Furniture</strong><br />
                Abdul Razzaq Mohiddin Abdulla<br />
                Warehouse Shed No-1 Opp. Pedal Art<br />
                Al Quoz Industrial Area – 1, Dubai
              </div>
            </div>
            
            <div className="contact-item">
              <FaEnvelope className="contact-icon" />
              <div>
                <a href="mailto:info@designcraft.ae">info@designcraft.ae</a>
              </div>
            </div>
            
            <div className="contact-item">
              <FaPhone className="contact-icon" />
              <div>
                <a href="tel:+971501989883">+971 50 198 9883</a>
              </div>
            </div>
          </div>

          <div className="cta-buttons">
            <Link 
              to="/contact" 
              className="cta-button primary"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              Contact Us
            </Link>
            <Link 
              to="/faq" 
              className="cta-button secondary"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              View FAQ
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 